export const TERMS_LAST_UPDATED = 'June 9, 2026';

export interface TermsSection {
  title: string;
  paragraphs: string[];
  emphasis?: boolean;
}

export const TERMS_INTRO = [
  'Welcome to Bag Tag ("App"), owned and operated by Timberline Ventures LLC ("Company," "we," "our," or "us").',
  'These Terms of Service ("Terms") govern your access to and use of the App, website, services, content, and features provided by Bag Tag.',
  'By creating an account, accessing, or using the App, you agree to be bound by these Terms. If you do not agree to these Terms, do not use the App.',
];

export const TERMS_SECTIONS: TermsSection[] = [
  {
    title: '1. Eligibility',
    paragraphs: [
      'You must be at least eighteen (18) years of age to use the App.',
      'By using the App, you represent and warrant that: you are at least 18 years old; you have the legal authority to enter into these Terms; and you will comply with all applicable laws and regulations.',
    ],
  },
  {
    title: '2. Description of Services',
    paragraphs: [
      'Bag Tag is a golf-focused social platform that may include: golf trip management; event creation; scorekeeping; leaderboards; handicaps; side game tracking; settlement recommendations; social feeds; user profiles; messaging and commenting; photos and media sharing; and statistics and performance tracking.',
      'Features may change, be modified, or discontinued at any time.',
    ],
  },
  {
    title: '3. User Accounts',
    paragraphs: [
      'To access certain features, you may be required to create an account.',
      'You agree to: provide accurate information; keep your information current; maintain the confidentiality of your account credentials; and accept responsibility for all activities occurring under your account.',
      'You are responsible for any activity conducted through your account.',
    ],
  },
  {
    title: '4. User Conduct',
    paragraphs: [
      'You agree not to: violate any law or regulation; harass, threaten, intimidate, or abuse other users; post false, misleading, or fraudulent information; upload offensive, defamatory, obscene, or unlawful content; impersonate another individual or organization; attempt to gain unauthorized access to any system or account; interfere with the operation or security of the App; or use the App to promote illegal gambling activities.',
      'We reserve the right to suspend or terminate accounts that violate these Terms.',
    ],
  },
  {
    title: '5. User Content',
    paragraphs: [
      'You retain ownership of content you submit to the App, including photos, videos, comments, scores, profile information, event information, messages, and other user-generated content.',
      'By submitting content, you grant Timberline Ventures LLC a worldwide, non-exclusive, royalty-free, transferable license to use, host, store, reproduce, modify, display, distribute, and publish such content solely for the purpose of operating, improving, marketing, and providing the App.',
      'You represent and warrant that you have all necessary rights to submit such content.',
    ],
  },
  {
    title: '6. Golf Scores, Statistics, and Handicaps',
    paragraphs: [
      'Bag Tag relies on information entered by users and information received from third-party sources.',
      'The Company does not guarantee the accuracy of scores, statistics, handicaps, rankings, leaderboards, tournament results, or historical records.',
      'Users are solely responsible for verifying the accuracy of all information.',
    ],
  },
  {
    title: '7. Side Games and Settlement Recommendations',
    paragraphs: [
      'The App may provide optional features that assist users in tracking games, side contests, scores, standings, and settlement calculations. All settlement calculations are estimates provided solely for convenience.',
      'Users are solely responsible for verifying calculations, confirming balances, determining payment obligations, and resolving disputes.',
      'The Company is not responsible for any errors, disagreements, losses, or damages arising from settlement calculations or user-arranged contests.',
    ],
  },
  {
    title: '8. No Gambling Services',
    paragraphs: [
      'Bag Tag is not a sportsbook, casino, gambling operator, wagering service, or gaming platform.',
      'The Company does not accept wagers, hold funds, process bets, facilitate gambling transactions, operate contests for money, act as an escrow provider, or collect or distribute winnings.',
      'Any financial arrangements, contests, games, wagers, side bets, or settlements are solely between users. Users are responsible for ensuring compliance with all applicable laws in their jurisdiction.',
    ],
  },
  {
    title: '9. Payments Between Users',
    paragraphs: [
      'The Company does not participate in payments between users. Any payments facilitated through third-party services, including peer-to-peer payment providers, are solely between participating users.',
      'The Company is not responsible for non-payment, overpayment, collection efforts, fraud, chargebacks, or payment disputes.',
    ],
  },
  {
    title: '10. User Disputes',
    paragraphs: [
      'The Company is not responsible for disputes between users, including disputes relating to scores, handicaps, event participation, team selection, tournament results, side games, settlements, payments, user conduct, or user-generated content.',
      'Users agree to resolve disputes directly with one another.',
    ],
  },
  {
    title: '11. Intellectual Property',
    paragraphs: [
      'All rights, title, and interest in the App, including software, designs, features, logos, trademarks, branding, graphics, and functionality, remain the exclusive property of Timberline Ventures LLC or its licensors.',
      'You may not copy, modify, distribute, reverse engineer, sell, license, or exploit any portion of the App without prior written consent.',
    ],
  },
  {
    title: '12. Third-Party Services',
    paragraphs: [
      'The App may integrate with third-party services and platforms. The Company is not responsible for third-party websites, payment providers, handicap services, social platforms, mapping providers, analytics providers, or other external services.',
      'Your use of such services is governed by their respective terms and policies.',
    ],
  },
  {
    title: '13. Account Suspension and Termination',
    paragraphs: [
      'We may suspend, restrict, or terminate any account at any time for violations of these Terms, suspected fraud, illegal activity, abuse of the platform, or conduct harmful to users or the Company.',
      'Users may delete their account at any time through available account settings or by contacting support.',
    ],
  },
  {
    title: '14. Disclaimer of Warranties',
    paragraphs: [
      'THE APP IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE COMPANY DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, ACCURACY, RELIABILITY, AND AVAILABILITY.',
      'THE COMPANY DOES NOT GUARANTEE THAT THE APP WILL OPERATE WITHOUT INTERRUPTION, THAT THE APP WILL BE ERROR-FREE, THAT DATA WILL ALWAYS BE AVAILABLE, THAT CALCULATIONS WILL BE ACCURATE, OR THAT USER CONTENT WILL BE PRESERVED. USE OF THE APP IS AT YOUR OWN RISK.',
    ],
    emphasis: true,
  },
  {
    title: '15. Limitation of Liability',
    paragraphs: [
      'TO THE MAXIMUM EXTENT PERMITTED BY LAW, TIMBERLINE VENTURES LLC, ITS MEMBERS, OFFICERS, EMPLOYEES, CONTRACTORS, AFFILIATES, AGENTS, SUCCESSORS, AND ASSIGNS SHALL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, LOST PROFITS, LOST DATA, BUSINESS INTERRUPTION, USER DISPUTES, PAYMENT DISPUTES, TOURNAMENT DISPUTES, SCOREKEEPING ERRORS, OR SETTLEMENT ERRORS.',
      'IN NO EVENT SHALL THE COMPANY\'S TOTAL LIABILITY EXCEED ONE HUNDRED U.S. DOLLARS ($100).',
    ],
    emphasis: true,
  },
  {
    title: '16. Indemnification',
    paragraphs: [
      'You agree to defend, indemnify, and hold harmless Timberline Ventures LLC and its affiliates, officers, employees, agents, contractors, and representatives from and against any claims, damages, liabilities, costs, expenses, and attorneys\' fees arising from your use of the App, your content, your violation of these Terms, your interactions with other users, or any contests, games, wagers, or financial arrangements you participate in.',
    ],
  },
  {
    title: '17. Modifications to the App',
    paragraphs: [
      'The Company reserves the right to modify, suspend, discontinue, or remove any feature of the App at any time without notice. The Company shall not be liable for any resulting loss or inconvenience.',
    ],
  },
  {
    title: '18. Changes to These Terms',
    paragraphs: [
      'We may update these Terms from time to time. Continued use of the App after changes become effective constitutes acceptance of the revised Terms.',
    ],
  },
  {
    title: '19. Governing Law',
    paragraphs: [
      'These Terms shall be governed by and interpreted under the laws of the Commonwealth of Virginia, without regard to conflict of law principles. Any dispute arising under these Terms shall be brought exclusively in the state or federal courts located in Virginia.',
    ],
  },
  {
    title: '20. Contact Information',
    paragraphs: [
      'Bag Tag — Owned and Operated by Timberline Ventures LLC',
      'Support Email: support@bagtagapp.com',
      'Privacy Email: privacy@bagtagapp.com',
      'Website: bagtagapp.com',
    ],
  },
  {
    title: '21. Acceptance of Terms',
    paragraphs: [
      'By creating an account, accessing, or using Bag Tag, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.',
    ],
  },
];
